import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { put } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { Project, ProjectLabel } from '../../../types';

type UpdateProjectParams = {
  projectLocator: string;
  data: Partial<Project>;
  query?: Record<string, unknown>;
};

async function updateProject({ projectLocator, data, query }: UpdateProjectParams): Promise<Project> {
  // Convert list of Label objects to list of ids
  const updatedData = { ...data };
  if (updatedData.labels && Array.isArray(updatedData.labels)) {
    updatedData.labels = updatedData.labels.map((value) => value.id) as unknown as ProjectLabel[];
  }

  const response = await put<Partial<Project>, Project>(
    `/api/projects/${nuc(projectLocator)}`,
    updatedData,
    { query },
  );

  // Type assertion is safe here - successful API responses always include data
  return response.data as Project;
}

/**
 * Hook for updating a project.
 *
 * This hook provides a mutation function to update a project and handles
 * success/error messaging and invalidates relevant queries on success.
 *
 * @example
 * ```typescript
 * const { mutateAsync } = useUpdateProject();
 *
 * await mutateAsync({
 *   projectLocator: "git+github.com/owner/repo",
 *   data: {
 *     title: "New Project Title",
 *     description: "Updated description"
 *   }
 * });
 * ```
 *
 * @returns A React Query mutation object with mutateAsync function
 *
 * @see When migrating from Reflux: This hook automatically shows success/error toasts via useToastedMutation.
 * Be careful when replacing old Reflux update calls that might trigger the toasts.
 * Double-check that toasting is working correctly after migration.
 */
export default function useUpdateProject() {
  return useToastedMutation({
    mutationFn: updateProject,
    successToastMessage: 'Project updated successfully',
    errorToastMessage: 'Failed to update project',
    onSuccess: (_data, variables) => {
      // Invalidate the project query so that the updated data is refetched
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectLocator] });
    },
  });
}
